import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Trash2, Plus, Minus, ShoppingCart, CheckCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useCart } from '../hooks/useCart'
import api from '../services/api'
import { getApiErrorKey } from '../utils/apiError'
import { formatCurrency } from '../utils/helpers'

export default function Cart() {
  const { t } = useTranslation()
  const { cart, updateQuantity, removeFromCart, clearCart } = useCart()
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [orderCreated, setOrderCreated] = useState(false)
  const navigate = useNavigate()

  const handleSubmitOrder = async () => {
    if (cart.items.length === 0) return

    setSubmitting(true)
    try {
      const orderData = {
        lines: cart.items.map((item) => ({
          product_id: item.product.id,
          quantity: item.quantity,
          unit_price: item.product.price,
          description: item.product.label,
        })),
        notes: notes || undefined,
      }

      await api.createOrder(orderData)
      setOrderCreated(true)
      clearCart()

      // Rediriger vers les commandes après 2 secondes
      setTimeout(() => {
        navigate('/orders')
      }, 2000)
    } catch (error: unknown) {
      const apiErr = error as { response?: { data?: { error?: string } } }
      console.error('Erreur création commande:', error)
      console.error('Error structure:', JSON.stringify(apiErr.response?.data, null, 2))

      // Extraire le message d'erreur
      let errorMessage = t('cart.orderError')

      // Convertir toute la réponse en string pour vérifier le stock
      const errorString = JSON.stringify(apiErr.response?.data || error)

      // Vérifier si c'est une erreur de stock
      if (
        errorString.includes('Stock is not enough') ||
        errorString.includes('Stock insuffisant')
      ) {
        errorMessage = t('cart.stockError')
      } else {
        errorMessage = getApiErrorKey(error, 'cart.orderError')
      }

      alert(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  if (orderCreated) {
    return (
      <div className="flex flex-col items-center justify-center h-64 animate-fade-in">
        <CheckCircle className="w-16 h-16 text-green-600 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('cart.orderSuccess')}</h2>
        <p className="text-gray-600">Redirection vers vos commandes...</p>
      </div>
    )
  }

  if (cart.items.length === 0) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center space-x-4">
          <Link to="/catalog" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">{t('cart.title')}</h1>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('cart.empty')}</h2>
          <p className="text-gray-600 mb-6">{t('cart.browseProducts')}</p>
          <Link
            to="/catalog"
            className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            {t('cart.browseProducts')}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/catalog" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('cart.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {cart.items.length} article{cart.items.length > 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Cart items */}
        <div className="lg:col-span-2 space-y-4">
          {cart.items.map((item) => (
            <div
              key={item.product.id}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
            >
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                  <ShoppingCart className="w-8 h-8 text-gray-400" />
                </div>

                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-gray-900">{item.product.label}</h3>
                      <p className="text-sm text-gray-500">Réf: {item.product.ref}</p>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.product.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title={t('common.delete')}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                        className="p-1 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-12 text-center font-medium">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                        className="p-1 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="text-right">
                      <p className="text-sm text-gray-500">Prix unitaire TTC</p>
                      <p className="font-semibold text-gray-900">
                        {formatCurrency(item.product.price_ttc)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                    <p className="text-sm text-gray-500">Sous-total TTC</p>
                    <p className="text-lg font-bold text-primary-600">
                      {formatCurrency(item.product.price_ttc * item.quantity)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Order summary */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sticky top-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Récapitulatif</h2>

            <div className="space-y-3 mb-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Total HT</span>
                <span className="font-medium text-gray-900">
                  {formatCurrency(cart.total_excl_tax)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">TVA</span>
                <span className="font-medium text-gray-900">{formatCurrency(cart.total_vat)}</span>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                <span className="text-lg font-semibold text-gray-900">Total TTC</span>
                <span className="text-lg font-bold text-primary-600">
                  {formatCurrency(cart.total_incl_tax)}
                </span>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (optionnel)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                placeholder="Ajouter des instructions ou remarques..."
              />
            </div>

            <button
              onClick={handleSubmitOrder}
              disabled={submitting}
              className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ShoppingCart className="w-5 h-5" />
              <span>{submitting ? t('cart.ordering') : t('cart.placeOrder')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
